import React from "react";
import { isHarmlessScannerError } from "../lib/scannerErrors";

/**
 * ErrorBoundary that silently swallows known-harmless scanner library errors
 * (html5-qrcode play() lifecycle noise) while still surfacing real errors.
 *
 * Harmless errors: re-render children as if nothing happened.
 * Real errors: render the fallback UI.
 *
 * The key insight: react-error-overlay (dev mode) hooks into React's
 * internal error stream and bypasses window.unhandledrejection. Only an
 * ErrorBoundary at the React level can suppress its display.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasRealError: false, error: null, swallowed: false };
  }

  static getDerivedStateFromError(error) {
    // Harmless: silently swallow this render. We render `null` for this
    // subtree until parent forces a remount. This is the right behavior for
    // html5-qrcode's lifecycle noise — the error is from an already-stale
    // <video> ref that React was tearing down anyway.
    if (isHarmlessScannerError(error)) {
      return { hasRealError: false, error: null, swallowed: true };
    }
    return { hasRealError: true, error, swallowed: false };
  }

  componentDidCatch(error, errorInfo) {
    if (isHarmlessScannerError(error)) {
      // Silently swallow. Do NOT log to console (it would re-trigger overlay).
      return;
    }
    // For real errors, log so engineers can debug.
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  reset = () => {
    this.setState({ hasRealError: false, error: null, swallowed: false });
  };

  render() {
    if (this.state.hasRealError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-napoli-marble px-6">
          <div className="max-w-md text-center">
            <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood">
              Unexpected error
            </p>
            <h1 className="mt-3 font-display font-bold text-[28px] text-napoli-espresso">
              Something went wrong.
            </h1>
            <p className="mt-3 font-body text-[14px] text-napoli-espresso-soft">
              {this.state.error?.message || "We hit an unexpected issue."}
            </p>
            <button
              type="button"
              onClick={this.reset}
              data-testid="error-boundary-reset"
              className="mt-6 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 px-6 hover:bg-napoli-oxblood-dark transition-colors"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    if (this.state.swallowed) {
      // Harmless render-phase error already swallowed. Blank subtree until
      // parent re-renders us (e.g. route change). In practice, scanner
      // lifecycle errors are async and never hit this branch — but if one
      // ever DOES reach the boundary during render, we don't loop on retry.
      return null;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
